import React, { useEffect, useMemo, useRef } from 'react';
import { Container, Header, Plate, Row, Text } from 'features/shared/ui/Page';
import { QuestionWrap, Question, Answer, PolicyLink, AnswerVertical } from './SurveysStyled';
import RadioButton from '@gr-monorepo/common/ui/RadioButton';
import Checkbox from '@gr-monorepo/common/ui/Checkbox';
import { Button, Space } from '@gr-monorepo/common/ui';
import { alertPopup } from '@gr-monorepo/common/ui/AlertPopup';
import Textarea from '@gr-monorepo/common/ui/Textarea/Textarea';
import useFormState from '@gr-monorepo/common/utils/useFormState';
import { SurveysApi } from 'api';
import { useSurveysContext } from './SurveysContext';
import Tooltip from 'features/shared/Tooltip';
import { LogClick } from 'features/shared/ClickStream';

type MonitoringTSORState = {
  answer1: string[];
  answer1Comment5: string;
  answer1Comment6: string;
  answer2: string;
  answer2Comment: string;
  answer3: string;
  answer4: string;
  answer4Comment: string;
  answer5: string;
  answer6: string;
  answer7: string;
};
const monitoringTSORDefault = {
  answer1: [],
  answer1Comment5: '',
  answer1Comment6: '',
  answer2: '',
  answer2Comment: '',

  answer3: '',
  answer4: '',
  answer4Comment: '',
  answer5: '',
  answer6: '',
  answer7: '',
};

const surveyName = 'survey_monitoring_TSOR';
const surveyTitle = 'Опрос о сервисах для мониторинга ЦОР';

const clickAnswerEventName = surveyName + '_click_answer';

const useMonitoringTSORState = () => useFormState<MonitoringTSORState>(monitoringTSORDefault);
const validate = (values: MonitoringTSORState) => {
  return Object.values(values).some((el) => !!el);
};
const MonitoringTSOR = () => {
  const pending = useRef(false);
  const { enabledSendAnswers, setAnswered, setVisited, visitedPage } = useSurveysContext();
  const state = useMonitoringTSORState();
  const tooltipTitle = useMemo(() => {
    if (!enabledSendAnswers) {
      return 'Ваши ответы были приняты. Спасибо за участие в опросе.';
    }
    const isValid = validate(state.values);
    if (!isValid) {
      return 'Заполните форму пред отправкой';
    }
    return null;
  }, [state.values, enabledSendAnswers]);
  const sendAnswers = async () => {
    if (pending.current) return;
    pending.current = true;
    const isValid = validate(state.values);
    if (!isValid) {
      pending.current = false;
      return;
    }
    try {
      await SurveysApi.sendResponse({
        responses: (Object.keys(state.values) as (keyof MonitoringTSORState)[]).map((el) => ({
          question: el,
          answer:
            state.values[el] instanceof Array
              ? (state.values[el] as string[]).join!(', ')
              : (state.values[el] as string) || null,
        })),
        survey_id: 'monitoring_TSOR',
      });
      setAnswered();
      await alertPopup(
        <span style={{ fontSize: 20, lineHeight: '28px' }}>
          Ваши ответы были приняты. <br />
          Спасибо за участие в опросе.
        </span>,
        {
          submit: { text: 'Закрыть' },
          durationAutoClose: 5000,
        },
      );
    } catch (e) {}
    pending.current = false;
  };
  useEffect(() => {
    if (!visitedPage) {
      setVisited();
    }
  }, []);

  return (
    <Container>
      <Header>Опрос о сервисах для мониторинга ЦОР в образовательных учреждениях</Header>
      <Space size={8} axis="vertical" />
      <Text style={{ paddingLeft: 24 }}>
        Пожалуйста, пройдите короткий опрос о сервисах, которые помогают отслеживать использование цифровых
        образовательных ресурсов (ЦОР) в учреждениях. Ответы позволят сделать кабинет региона и муниципалитета еще
        удобнее и эффективнее для вас.
      </Text>
      <Space size={32} axis="vertical" />
      <Plate>
        <QuestionWrap>
          <Question>1. Какие сервисы для мониторинга ЦОР вы используете? Можно выбрать несколько вариантов.</Question>
          <Answer>
            <Space size={8} />
            <AnswerVertical>
              {[
                'ФГИС «Моя школа»',
                'Сферум',
                'Собственный сервис',
                'Не использую — напишу причину',
              ].map((el, i, arr) => (
                <>
                  <LogClick
                    eventType={clickAnswerEventName + '_1'}
                    eventInfo={`Опрос|${surveyTitle}: клик в ответ 1 ${el}`}
                  >
                    <Checkbox
                      value={state.values.answer1.includes(el)}
                      onChange={() =>
                        state.setValue('answer1')(
                          state.values.answer1.includes(el)
                            ? state.values.answer1.filter((v) => v !== el)
                            : [...state.values.answer1, el],
                        )
                      }
                    >
                      {el}
                    </Checkbox>
                  </LogClick>
                  <Space size={8} axis="vertical" />
                </>
              ))}
              <LogClick
                eventType={clickAnswerEventName + '_1_comment'}
                eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 1`}
              >
                <Textarea
                  placeholder="Несколько предложений в свободной форме."
                  style={{ width: '100%', height: 108 }}
                  value={state.values.answer1Comment5}
                  onChange={state.setValue('answer1Comment5')}
                />
              </LogClick>
              <Space size={16} axis="vertical" />
              {['Другое — напишу свой вариант'].map((el, i, arr) => (
                <>
                  <LogClick
                    eventType={clickAnswerEventName + '_1'}
                    eventInfo={`Опрос|${surveyTitle}: клик в ответ 1 ${el}`}
                  >
                    <Checkbox
                      value={state.values.answer1.includes(el)}
                      onChange={() =>
                        state.setValue('answer1')(
                          state.values.answer1.includes(el)
                            ? state.values.answer1.filter((v) => v !== el)
                            : [...state.values.answer1, el],
                        )
                      }
                    >
                      {el}
                    </Checkbox>
                  </LogClick>
                  <Space size={8} axis="vertical" />
                </>
              ))}
              <LogClick
                eventType={clickAnswerEventName + '_1_comment'}
                eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 1`}
              >
                <Textarea
                  placeholder="Несколько предложений в свободной форме."
                  style={{ width: '100%', height: 108 }}
                  value={state.values.answer1Comment6}
                  onChange={state.setValue('answer1Comment6')}
                />
              </LogClick>
            </AnswerVertical>
          </Answer>
        </QuestionWrap>
        <QuestionWrap>
          <Question>2. Как часто вы используете в работе сервисы для мониторинга ЦОР?</Question>
          <Answer>
            <Space size={8} />
            <AnswerVertical>
              {[
                'Ежедневно',
                'Еженедельно',
                'Ежемесячно',
                'Несколько раз в квартал',
                'Несколько раз в полгода',
                'Несколько раз в год',
                'Другое — напишу свой вариант',
              ].map((el, i, arr) => (
                <>
                  <LogClick
                    eventType={clickAnswerEventName + '_2'}
                    eventInfo={`Опрос|${surveyTitle}: клик в ответ 2 ${el}`}
                  >
                    <RadioButton value={state.values.answer2 === el} onChange={() => state.setValue('answer2')(el)}>
                      {el}
                    </RadioButton>
                  </LogClick>
                  <Space size={8} axis="vertical" />
                </>
              ))}
              <LogClick
                eventType={clickAnswerEventName + '_2_comment'}
                eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 2`}
              >
                <Textarea
                  placeholder="Несколько предложений в свободной форме."
                  style={{ width: '100%', height: 108 }}
                  value={state.values.answer2Comment}
                  onChange={state.setValue('answer2Comment')}
                />
              </LogClick>
            </AnswerVertical>
          </Answer>
        </QuestionWrap>
        <QuestionWrap>
          <Question>3. Какие функции вы используете в сервисах для мониторинга ЦОР?</Question>
          <Answer>
            <Space size={8} />
            <LogClick
              eventType={clickAnswerEventName + '_3'}
              eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 3`}
            >
              <Textarea
                placeholder="Несколько предложений в свободной форме."
                style={{ width: '100%', height: 108 }}
                value={state.values.answer3}
                onChange={state.setValue('answer3')}
              />
            </LogClick>
          </Answer>
        </QuestionWrap>
        <QuestionWrap>
          <Question>4. Насколько просто и удобно вам использовать сервисы для мониторинга ЦОР?</Question>
          <Answer>
            <Space size={8} />
            <AnswerVertical>
              {[
                'Очень просто и удобно',
                'В целом просто и удобно, но бывают трудности',
                'Часто возникают трудности',
                'Мне совсем непонятно, как использовать эти сервисы',
                'Другое — напишу свой вариант',
              ].map((el, i, arr) => (
                <>
                  <LogClick
                    eventType={clickAnswerEventName + '_4'}
                    eventInfo={`Опрос|${surveyTitle}: клик в ответ 4 ${el}`}
                  >
                    <RadioButton value={state.values.answer4 === el} onChange={() => state.setValue('answer4')(el)}>
                      {el}
                    </RadioButton>
                  </LogClick>
                  <Space size={8} axis="vertical" />
                </>
              ))}
              <LogClick
                eventType={clickAnswerEventName + '_4_comment'}
                eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 4`}
              >
                <Textarea
                  placeholder="Несколько предложений в свободной форме."
                  style={{ width: '100%', height: 108 }}
                  value={state.values.answer4Comment}
                  onChange={state.setValue('answer4Comment')}
                />
              </LogClick>
            </AnswerVertical>
          </Answer>
        </QuestionWrap>
        <QuestionWrap>
          <Question>5. Какие преимущества сервисов для мониторинга ЦОР вы можете выделить?</Question>
          <Answer>
            <Space size={8} />
            <LogClick
              eventType={clickAnswerEventName + '_5'}
              eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 5`}
            >
              <Textarea
                placeholder="Несколько предложений в свободной форме."
                style={{ width: '100%', height: 108 }}
                value={state.values.answer5}
                onChange={state.setValue('answer5')}
              />
            </LogClick>
          </Answer>
        </QuestionWrap>
        <QuestionWrap>
          <Question>6. С какими недостатками сервисов для мониторинга ЦОР вы сталкивались?</Question>
          <Answer>
            <Space size={8} />
            <LogClick
              eventType={clickAnswerEventName + '_6'}
              eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 6`}
            >
              <Textarea
                placeholder="Несколько предложений в свободной форме."
                style={{ width: '100%', height: 108 }}
                value={state.values.answer6}
                onChange={state.setValue('answer6')}
              />
            </LogClick>
          </Answer>
        </QuestionWrap>
        <QuestionWrap>
          <Question>
            7. Напишите имя, e-mail и телефон вашего коллеги, который регулярно использует в работе сервисы для
            мониторинга ЦОР. Мы предложим ему уделить нам время для более подробной беседы на эту тему — чтобы сделать
            сервис максимально полезным.
          </Question>
          <Answer>
            <Space size={8} />
            <LogClick
              eventType={clickAnswerEventName + '_7'}
              eventInfo={`Опрос|${surveyTitle}: клик в свободный коммент 7`}
            >
              <Textarea
                placeholder="Имя специалиста, номер телефона и адрес электронной почты."
                style={{ width: '100%', height: 108 }}
                value={state.values.answer7}
                onChange={state.setValue('answer7')}
              />
            </LogClick>
          </Answer>
        </QuestionWrap>
        <Row>
          <span>
            Нажимая «Отправить», вы соглашаетесь с{' '}
            <LogClick
              eventType={clickAnswerEventName + '_open_privacy'}
              eventInfo={`Нажал открыть политику обработки ПД в опросе ${surveyTitle}`}
            >
              <PolicyLink href="https://uchi.ru/privacy" target="_blank">
                политикой обработки персональных данных
              </PolicyLink>{' '}
            </LogClick>
          </span>
          <Space size={24} />
          <LogClick
            eventType={clickAnswerEventName + '_click_send'}
            eventInfo={`Опрос|${surveyTitle}: нажал отправить`}
          >
            {tooltipTitle ? (
              <Tooltip title={tooltipTitle} minWidth={20} maxWidth={200} arrow placement="top">
                <div>
                  <Button style={{ width: 240 }} color="green" isDisabled>
                    ОТПРАВИТЬ
                  </Button>
                </div>
              </Tooltip>
            ) : (
              <div>
                <Button style={{ width: 240 }} color="green" onClick={sendAnswers}>
                  ОТПРАВИТЬ
                </Button>
              </div>
            )}
          </LogClick>
        </Row>
      </Plate>
    </Container>
  );
};

export default MonitoringTSOR;
